




































































































import { CancelTokenSource } from 'axios';
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import { Modal } from 'ant-design-vue';

import Toast from '@/mixins/toast';
import { EventBus, MM_APPLY_ASSIGNMENT_FILTERS } from '@/mixins/event-bus';

import CreateFooter from './blocks/CreateFooter.vue';
import MonitoringLevelHint from './blocks/MonitoringLevelHint.vue';
import ProductFiltersBlock from './blocks/ProductFiltersBlock.vue';
import ProductsTableBlock from './blocks/ProductsTableBlock.vue';
import TaskTypeBlock from './blocks/TaskTypeBlock.vue';
import ZoneBlock from './blocks/ZoneBlock.vue';
import NoAuditorPopup from './NoAuditorPopup.vue';

import { createCancelSource } from '../../api/mmHttp';
import { fetchTasksInfo } from '../../api/catalog';
import { fetchZoneEstimate } from '../../api/tasks';
import { hasSubcategories } from '../../core/clientProfile';
import { toExplicitMonitoringFilters } from '../../core/monitoringLevel';
import { isCanceled, toMmError } from '../../core/errors';
import {
	createSelectionState,
	isSelectionEmpty,
	MmSelectionState,
	resetSelection,
	selectedCount,
} from '../../core/selection';
import { getSessionStore } from '../../core/session';
import { MmNoAuditorErrorBody, MmSelectOption, MmSkuKey } from '../../types/domain';
import { MmTaskFilters } from '../../types/dto';
import { MmTaskType } from '../../types/enums';
import {
	MmTaskCreateFormState,
	MmTaskCreateLevels,
	MmTaskCreateZone,
	MmTaskCreateFormatCatalog,
	prepareFiltersFromParts,
	submitTaskCreate,
	withCreateFormatExpansion,
} from './buildTaskCreatePayload';
import {
	clearTaskCreateDraft,
	isDraftWorthKeeping,
	MmTaskCreateDraft,
	saveTaskCreateDraft,
	takeTaskCreateDraft,
} from './taskCreateDraft';
import { getTaskTypeProfile, MmTaskTypeProfile, resolveCreateBlockReasonKey } from './taskTypeProfile';

const KEY = 'priceMonitoring.mobileMonitoring.taskForm.';

const COMMENT_MAX_LENGTH = 2000;

@Component({
	components: {
		CreateFooter,
		MonitoringLevelHint,
		NoAuditorPopup,
		ProductFiltersBlock,
		ProductsTableBlock,
		TaskTypeBlock,
		ZoneBlock,
	},
})
export default class TaskCreate extends Mixins(Toast) {
	@Prop({ default: null }) readonly tradingId!: number | null;

	@Prop({ default: false }) readonly isActive!: boolean;

	/** В инициализаторе поля type проп не читаем: порядок создания data и props не гарантирован */
	@Prop({ default: null }) readonly initialType!: MmTaskType | null;

	COMMENT_MAX_LENGTH = COMMENT_MAX_LENGTH;

	type: MmTaskType | null = null;

	/** Сырые значения оси: 0 означает «Все» */
	zone: MmTaskCreateZone = { formats: [], regions: [], clusters: [] };

	/** Id форматов из справочника: `null` — ещё грузится. */
	loadedFormatIds: MmTaskCreateFormatCatalog = null;

	levels: MmTaskCreateLevels = { groups: [], categories: [], subCategories: [] };

	comment: string = '';

	isPriceCheckerEnabled: boolean = false;

	selection: MmSelectionState<MmSkuKey> = createSelectionState<MmSkuKey>();

	productsTotal: number = 0;

	isSubmitting: boolean = false;

	/** Есть ли у клиента подкатегории по ответу tasks/info; null — ответа ещё не было */
	tasksInfoFlag: boolean | null = null;

	subcategoryOptions: MmSelectOption[] = [];

	isSubcategoriesLoading: boolean = false;

	estimateCount: number | null = null;

	estimatePending: boolean = false;

	productsLoading: boolean = false;

	noAuditorBody: MmNoAuditorErrorBody | null = null;

	isNoAuditorVisible: boolean = false;

	/** Инкремент ключа пересоздаёт блок после отмены: иначе селект покажет не то, что в модели формы */
	zoneRenderKey: number = 0;

	levelsRenderKey: number = 0;

	zonePending: boolean = false;

	zoneDraft: MmTaskCreateZone = { formats: [], regions: [], clusters: [] };

	levelsDraft: MmTaskCreateLevels = { groups: [], categories: [], subCategories: [] };

	needsInfoReload: boolean = false;

	private isLeavingDeliberately: boolean = false;

	private infoSource: CancelTokenSource | null = null;

	private estimateSource: CancelTokenSource | null = null;

	private submitSource: CancelTokenSource | null = null;

	get profile(): MmTaskTypeProfile | null {
		return getTaskTypeProfile(this.type);
	}

	/** Блок нужен только KVI: в нём живёт чекбокс «С прайс-чекером», у остальных типов это текст */
	get showTypeBlock(): boolean {
		return this.profile !== null && this.profile.hasPriceChecker === true;
	}

	get formState(): MmTaskCreateFormState {
		return {
			type: this.type !== null ? this.type : MmTaskType.CONTINUOUS,
			zone: this.zone,
			levels: this.levels,
			comment: this.comment,
			isPriceCheckerEnabled: this.isPriceCheckerEnabled,
			selection: this.selection,
		};
	}

	get preparedFilters(): MmTaskFilters {
		/** Считаем от зоны и уровней, а не от всей формы: иначе ввод комментария ронял бы запрос товаров */
		return prepareFiltersFromParts(this.zone, this.levels);
	}

	get outboundFilters(): MmTaskFilters {
		return withCreateFormatExpansion(this.preparedFilters, this.loadedFormatIds);
	}

	get monitoringHintFilters(): MmTaskFilters {
		return toExplicitMonitoringFilters(prepareFiltersFromParts(this.zoneDraft, this.levelsDraft));
	}

	get zoneFormatEmpty(): boolean {
		return prepareFiltersFromParts(this.zoneDraft, this.levels).formats.length === 0;
	}

	get clientHasSubcategories(): boolean {
		return hasSubcategories(this.tasksInfoFlag, this.subcategoryOptions);
	}

	get selectedProductsCount(): number {
		return selectedCount(this.selection, this.productsTotal);
	}

	get showSelectedCount(): boolean {
		return this.profile !== null && this.profile.hasProductsTable;
	}

	get blockReasonKey(): string | null {
		return resolveCreateBlockReasonKey({
			hasTradingNetwork: this.tradingId !== null,
			type: this.type,
			filters: this.preparedFilters,
			loadedFormatIds: this.loadedFormatIds,
			clientHasSubcategories: this.clientHasSubcategories,
			isSubcategoriesLoading: this.isSubcategoriesLoading,
			selectedProductsCount: this.selectedProductsCount,
			isSubmitting: this.isSubmitting,
		});
	}

	get footerEstimateCount(): number | null {
		return this.estimatePending ? null : this.estimateCount;
	}

	get estimatePendingText(): string {
		return this.$t(KEY + 'estimate', { count: '…' }) as string;
	}

	get productFiltersLoading(): boolean {
		if (this.profile !== null && this.profile.hasProductsTable) {
			return this.productsLoading;
		}

		return this.estimatePending;
	}

	get estimateHintText(): string {
		return this.$t(KEY + 'estimateHint') as string;
	}

	fieldLabelId(field: string): string {
		return 'sm-mobilemon-task-create-' + field + '-label-' + String((this as any)._uid);
	}

	created(): void {
		if (this.initialType !== null) {
			this.type = this.initialType;
		}

		this.restoreDraft();
		this.syncDraftCopies();

		this.$emit('type-change', this.type);
	}

	mounted(): void {
		if (this.isActive) {
			this.loadTasksInfo();

			return;
		}

		/** Пока вкладка не видна, в сеть не бьём: ant-design-vue не размонтирует однажды открытую вкладку */
		this.needsInfoReload = true;
	}

	@Watch('isActive')
	onActiveChanged(next: boolean): void {
		if (next === true && this.needsInfoReload) {
			this.needsInfoReload = false;
			this.loadTasksInfo();
		}
	}

	beforeDestroy(): void {
		this.cancelSource('infoSource');
		this.cancelSource('estimateSource');
		this.cancelSource('submitSource');
		this.persistDraft();
	}

	handleTypeChange(next: MmTaskType): void {
		if (next === this.type) {
			return;
		}

		this.withSelectionResetConfirm(KEY + 'confirmTypeChange', () => {
			this.type = next;

			this.$emit('type-change', next);

			const profile = getTaskTypeProfile(next);

			if (profile === null || !profile.hasPriceChecker) {
				this.isPriceCheckerEnabled = false;
			}

			this.refreshEstimate();
		});
	}

	handlePriceCheckerChange(next: boolean): void {
		this.isPriceCheckerEnabled = next;
	}

	handleZoneChange(next: MmTaskCreateZone): void {
		this.withSelectionResetConfirm(
			KEY + 'confirmFilterChange',
			() => {
				this.zone = next;
				this.refreshEstimate();
			},
			() => {
				this.zoneRenderKey += 1;
			},
		);
	}

	handleFormatsCatalog(next: MmTaskCreateFormatCatalog): void {
		this.loadedFormatIds = next;
		this.refreshEstimate();
	}

	handleZonePendingChange(pending: boolean): void {
		this.zonePending = pending === true;
	}

	handleZoneDraftChange(next: MmTaskCreateZone): void {
		this.zoneDraft = this.cloneZone(next);
	}

	handleLevelsDraftChange(next: MmTaskCreateLevels): void {
		this.levelsDraft = this.cloneLevelsState(next);
	}

	handleApplyAllFilters(nextLevels: MmTaskCreateLevels): void {
		const zoneBlock: any = this.$refs.zoneBlock;
		const nextZone: MmTaskCreateZone = zoneBlock && typeof zoneBlock.takeDraft === 'function'
			? zoneBlock.takeDraft()
			: this.cloneZone(this.zone);

		this.withSelectionResetConfirm(
			KEY + 'confirmFilterChange',
			() => {
				this.zone = nextZone;
				this.levels = this.cloneLevelsState(nextLevels);
				this.syncDraftCopies();
				this.refreshEstimate();
			},
			() => {
				this.zoneRenderKey += 1;
				this.levelsRenderKey += 1;
			},
		);
	}

	handleResetAllFilters(): void {
		const zoneBlock: any = this.$refs.zoneBlock;

		if (zoneBlock && typeof zoneBlock.resetDraft === 'function') {
			zoneBlock.resetDraft();
		}
	}

	handleLevelsChange(next: MmTaskCreateLevels): void {
		this.withSelectionResetConfirm(
			KEY + 'confirmFilterChange',
			() => {
				this.levels = next;
				this.refreshEstimate();
			},
			() => {
				this.levelsRenderKey += 1;
			},
		);
	}

	handleSubcategoriesState(payload: { loading: boolean; options: MmSelectOption[] }): void {
		this.isSubcategoriesLoading = payload.loading;
		this.subcategoryOptions = payload.options;
	}

	handleTotalChanged(total: number): void {
		this.productsTotal = total;
	}

	handleProductsLoadingChange(value: boolean): void {
		this.productsLoading = value === true;
	}

	handleSelectionChanged(): void {
		return;
	}

	/** Поиск сужает видимое, но в запрос создания не уезжает — отсюда то же подтверждение сброса */
	handleProductsSearchApplied(): void {
		this.withSelectionResetConfirm(
			KEY + 'confirmFilterChange',
			() => {
				this.resolveProductsSearch(true);
			},
			() => {
				this.resolveProductsSearch(false);
			},
		);
	}

	private resolveProductsSearch(confirmed: boolean): void {
		const block: any = this.$refs.productsTable;

		if (block && typeof block.resolvePendingSearch === 'function') {
			block.resolvePendingSearch(confirmed);
		}
	}

	handleCommentChange(event: any): void {
		this.comment = event && event.target ? String(event.target.value) : '';
	}

	/** Сбрасываем и массивы выбора, и режим, иначе исключения лягут на другой набор товаров */
	private withSelectionResetConfirm(messageKey: string, apply: () => void, onCancel?: () => void): void {
		const needsConfirm =
			this.profile !== null && this.profile.hasProductsTable && !isSelectionEmpty(this.selection);

		if (!needsConfirm) {
			apply();
			resetSelection(this.selection);
			this.productsTotal = 0;

			return;
		}

		const count = this.selectedProductsCount;

		Modal.confirm({
			title: this.$t(messageKey, { count: count }) as string,
			okText: this.$t(KEY + 'confirmContinue') as string,
			cancelText: this.$t('shared.actions.cancel') as string,
			onOk: () => {
				apply();
				resetSelection(this.selection);
				this.productsTotal = 0;
			},
			onCancel: () => {
				if (onCancel) {
					onCancel();
				}
			},
		});
	}

	/** Отдельной операции нет: признак выводим по непустоте справочника, отказ здесь штатен */
	private loadTasksInfo(): void {
		if (this.tradingId === null) {
			return;
		}

		this.cancelSource('infoSource');

		const source = createCancelSource();

		this.infoSource = source;

		fetchTasksInfo(this.tradingId, { cancelToken: source.token })
			.then((response) => {
				if (this.infoSource !== source) {
					return;
				}

				const body = response.data;

				this.tasksInfoFlag = body && typeof body.hasSubcategories === 'boolean' ? body.hasSubcategories : null;
			})
			.catch(() => {
				if (this.infoSource !== source) {
					return;
				}

				this.tasksInfoFlag = null;
			})
			.finally(() => {
				if (this.infoSource === source) {
					this.infoSource = null;
				}
			});
	}

	private refreshEstimate(): void {
		/** Отмена стоит до ранних выходов: иначе ответ прежнего фильтра перезапишет оценку количества */
		this.cancelSource('estimateSource');

		/** Оценку обнуляем только там, где её не из чего посчитать: иначе подвал дёргается на каждый запрос */
		if (this.tradingId === null) {
			this.estimateCount = null;
			this.estimatePending = false;

			return;
		}

		const filters = this.outboundFilters;

		if (filters.formats.length === 0) {
			this.estimateCount = null;
			this.estimatePending = false;

			return;
		}

		const source = createCancelSource();

		this.estimateSource = source;
		this.estimatePending = true;

		fetchZoneEstimate(filters, { cancelToken: source.token })
			.then((response) => {
				if (this.estimateSource !== source) {
					return;
				}

				const body = response.data;

				// Бэкенд отвечает голым числом, а не объектом; null означает «неизвестно»
				this.estimateCount = typeof body === 'number' ? body : null;
				this.estimatePending = false;
			})
			.catch(() => {
				if (this.estimateSource !== source) {
					return;
				}

				this.estimateCount = null;
				this.estimatePending = false;
			})
			.finally(() => {
				if (this.estimateSource === source) {
					this.estimateSource = null;
				}
			});
	}

	/** Защита от двойного клика: повтор сервер не отсеет, и выйдет вторая пачка заданий */
	handleSubmit(): void {
		if (this.isSubmitting || this.blockReasonKey !== null || this.tradingId === null) {
			return;
		}

		if (this.hasUnappliedFilters()) {
			this.commitPendingThenSubmit();

			return;
		}

		this.performSubmit();
	}

	/** Зону и уровни фиксируем до отправки, иначе запрос уйдёт со старыми значениями */
	private commitPendingThenSubmit(): void {
		const zoneBlock: any = this.$refs.zoneBlock;
		const nextZone: MmTaskCreateZone = zoneBlock && typeof zoneBlock.takeDraft === 'function'
			? zoneBlock.takeDraft()
			: this.cloneZone(this.zone);
		const nextLevels = this.cloneLevelsState(this.levelsDraft);

		this.withSelectionResetConfirm(
			KEY + 'confirmFilterChange',
			() => {
				this.zone = nextZone;
				this.levels = nextLevels;
				this.syncDraftCopies();
				this.refreshEstimate();
				this.performSubmit();
			},
		);
	}

	private performSubmit(): void {
		if (this.isSubmitting || this.blockReasonKey !== null || this.tradingId === null) {
			return;
		}

		this.isSubmitting = true;
		this.isNoAuditorVisible = false;
		this.noAuditorBody = null;

		this.cancelSource('submitSource');

		const source = createCancelSource();

		this.submitSource = source;

		submitTaskCreate(this.formState, this.tradingId, {
			cancelToken: source.token,
		}, this.loadedFormatIds)
			.then((response) => {
				if (this.submitSource !== source) {
					return;
				}

				const body = response.data;

				this.isLeavingDeliberately = true;
				this.resetForm();
				this.$emit('created', body);
			})
			.catch((error) => {
				if (this.submitSource !== source || isCanceled(error)) {
					return;
				}

				const mmError = toMmError(error, 'tasks/create');

				/** Отказ «нет аудитора» распознаём по форме тела: статус для него контрактом не задан */
				if (mmError.noAuditor !== null) {
					this.noAuditorBody = mmError.noAuditor;
					this.isNoAuditorVisible = true;

					return;
				}

				this.showError((this.$t(KEY + 'createError') as string) + ' ' + mmError.message);
			})
			.finally(() => {
				if (this.submitSource !== source) {
					return;
				}

				this.submitSource = null;
				this.isSubmitting = false;
			});
	}

	handleReset(): void {
		Modal.confirm({
			title: this.$t(KEY + 'confirmResetForm') as string,
			okText: this.$t(KEY + 'confirmContinue') as string,
			cancelText: this.$t('shared.actions.cancel') as string,
			onOk: () => {
				this.resetForm();
			},
		});
	}

	/** Ключи блоков инкрементируем: у зонных селектов состояние живёт внутри, обнуления модели мало */
	private resetForm(): void {
		clearTaskCreateDraft();
		this.type = this.initialType;
		this.zone = { formats: [], regions: [], clusters: [] };
		this.levels = { groups: [], categories: [], subCategories: [] };
		this.comment = '';
		this.isPriceCheckerEnabled = false;
		resetSelection(this.selection);
		this.productsTotal = 0;
		this.cancelSource('estimateSource');
		this.estimateCount = null;
		this.estimatePending = false;
		this.subcategoryOptions = [];
		this.isSubcategoriesLoading = false;
		this.zoneRenderKey += 1;
		this.levelsRenderKey += 1;
		this.syncDraftCopies();
		this.zonePending = false;
	}

	handleNoAuditorClose(): void {
		this.isNoAuditorVisible = false;
	}

	handleGoToAssignments(): void {
		this.isNoAuditorVisible = false;

		const filters = prepareFiltersFromParts(this.zone, this.levels);

		EventBus.$emit(MM_APPLY_ASSIGNMENT_FILTERS, {
			formats: filters.formats.slice(),
			regions: filters.regions.slice(),
			clusters: filters.clusters.slice(),
			onlyWithoutAuditor: true,
		});

		this.$emit('go-to-assignments');
	}

	handleBack(): void {
		if (this.isSubmitting) {
			return;
		}

		Modal.confirm({
			title: this.$t(KEY + 'confirmCancelTitle') as string,
			content: this.$t(KEY + 'confirmCancelText') as string,
			centered: true,
			okText: this.$t(KEY + 'confirmCancelOk') as string,
			cancelText: this.$t('shared.actions.cancel') as string,
			onOk: () => {
				this.closeCreateModal();
			},
		});
	}

	private closeCreateModal(): void {
		this.isLeavingDeliberately = true;
		clearTaskCreateDraft();
		this.$emit('back');
	}

	private restoreDraft(): void {
		const draft = takeTaskCreateDraft(this.tradingId);

		if (draft === null) {
			return;
		}

		this.type = draft.type;
		this.zone = draft.zone;
		this.levels = draft.levels;
		this.comment = draft.comment;
		this.isPriceCheckerEnabled = draft.isPriceCheckerEnabled;
		this.selection = draft.selection;
		this.productsTotal = draft.productsTotal;
	}

	private persistDraft(): void {
		if (this.isLeavingDeliberately || this.tradingId === null) {
			return;
		}

		/** Черновик сверяем с сессией: у уничтожаемого компонента значение сети ещё прежнее */
		if (getSessionStore().tradingNetworkId !== this.tradingId) {
			clearTaskCreateDraft();

			return;
		}

		const candidate: MmTaskCreateDraft = {
			tradingNetworkId: this.tradingId,
			type: this.type,
			zone: this.zone,
			levels: this.levels,
			comment: this.comment,
			isPriceCheckerEnabled: this.isPriceCheckerEnabled,
			selection: this.selection,
			productsTotal: this.productsTotal,
		};

		if (!isDraftWorthKeeping(candidate)) {
			clearTaskCreateDraft();

			return;
		}

		saveTaskCreateDraft(candidate);
	}

	private cancelSource(field: 'infoSource' | 'estimateSource' | 'submitSource'): void {
		const source = this[field];

		if (source !== null) {
			source.cancel();
			this[field] = null;
		}
	}

	private hasUnappliedFilters(): boolean {
		if (this.zonePending === true) {
			return true;
		}

		return !this.sameLevels(this.levels, this.levelsDraft);
	}

	private sameLevels(left: MmTaskCreateLevels, right: MmTaskCreateLevels): boolean {
		return this.sameIdList(left.groups, right.groups)
			&& this.sameIdList(left.categories, right.categories)
			&& this.sameIdList(left.subCategories, right.subCategories);
	}

	private sameIdList(left: number[], right: number[]): boolean {
		if (left.length !== right.length) {
			return false;
		}

		const a = left.slice().sort();
		const b = right.slice().sort();

		for (let i = 0; i < a.length; i += 1) {
			if (a[i] !== b[i]) {
				return false;
			}
		}

		return true;
	}

	private syncDraftCopies(): void {
		this.zoneDraft = this.cloneZone(this.zone);
		this.levelsDraft = this.cloneLevelsState(this.levels);
	}

	private cloneZone(source: MmTaskCreateZone): MmTaskCreateZone {
		return {
			formats: source.formats.slice(),
			regions: source.regions.slice(),
			clusters: source.clusters.slice(),
		};
	}

	private cloneLevelsState(source: MmTaskCreateLevels): MmTaskCreateLevels {
		return {
			groups: source.groups.slice(),
			categories: source.categories.slice(),
			subCategories: source.subCategories.slice(),
		};
	}
}
